import React, { useState } from 'react';
import PropTypes from 'prop-types';
import AccountService from '../../services/account.service';
import { Modal, Button, TextField, Box, Typography, Alert } from '@mui/material';

const BlockAccountModal = ({ open, onClose, accountId, isBlocked, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    setReason('');
    setError(null);
    onClose();
  };

  const handleBlock = () => {
    if (!reason.trim()) {
      setError('A reason is required.');
      return;
    }
    setLoading(true);
    setError(null);
    AccountService.blockAccount(accountId, reason.trim())
      .then(() => {
        setLoading(false);
        onSuccess();
        handleClose();
      })
      .catch((err) => {
        setLoading(false);
        setError(err?.response?.data?.message || 'Error blocking account.');
      });
  };

  const handleUnblock = () => {
    setLoading(true);
    setError(null);
    AccountService.unblockAccount(accountId)
      .then(() => {
        setLoading(false);
        onSuccess();
        handleClose();
      })
      .catch((err) => {
        setLoading(false);
        setError(err?.response?.data?.message || 'Error unblocking account.');
      });
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 480,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 1
        }}
      >
        <Typography variant="h6" component="h2" gutterBottom>
          {isBlocked ? 'Unblock account' : 'Block account (suspected fraud)'}
        </Typography>

        {!isBlocked && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This will prevent login, archive all active products and payment links in Stripe, and disable all websites for this account.
            </Alert>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this account being blocked?"
              sx={{ mt: 1 }}
            />
          </>
        )}

        {isBlocked && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Unblocking restores login, re-enables websites, and brings back the products the block archived (in Stripe too). Anything the user had deleted before the block stays deleted.
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={handleClose} disabled={loading}>Cancel</Button>
          {isBlocked ? (
            <Button variant="contained" color="primary" onClick={handleUnblock} disabled={loading}>
              Unblock
            </Button>
          ) : (
            <Button variant="contained" color="error" onClick={handleBlock} disabled={loading || !reason.trim()}>
              Block account
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

BlockAccountModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isBlocked: PropTypes.bool,
  onSuccess: PropTypes.func.isRequired
};

export default BlockAccountModal;
